import React from 'react';
import { Input } from './input';

const MAX_LEN = 40;

/**
 * SafeNameInput — a drop-in replacement for Input on "name" fields.
 * - Trims pasted content to MAX_LEN characters
 * - Strips characters that have no place in a name: < > { } [ ] \ | ` $ # ^ * ~ ; : ! @ = +
 * - Forwards all other props unchanged (placeholder, className, disabled, required, etc.)
 */
const sanitize = (val, max = MAX_LEN) =>
  val.replace(/<[^>]*>/g, '').replace(/[{}[\]\\/|`$#^*~;:!=+@]/g, '').slice(0, max);

export const SafeNameInput = React.forwardRef(
  ({ onChange, onPaste, maxLength = MAX_LEN, ...props }, ref) => {

    const handleChange = (e) => {
      const raw = e.target.value;
      const clean = sanitize(raw, maxLength);
      // Pass cleaned value through a synthetic event keeping all original properties
      onChange?.({ ...e, target: { ...e.target, value: clean } });
    };

    const handlePaste = (e) => {
      e.preventDefault();
      const pasted = (e.clipboardData || window.clipboardData).getData('text');
      const input = e.currentTarget;
      const current = input.value || '';
      const start = input.selectionStart ?? current.length;
      const end = input.selectionEnd ?? current.length;
      const merged = current.slice(0, start) + pasted + current.slice(end);
      const clean = sanitize(merged, maxLength);
      onChange?.({ target: { name: input.name, value: clean } });
      onPaste?.(e);
    };

    return (
      <Input
        ref={ref}
        maxLength={maxLength}
        onChange={handleChange}
        onPaste={handlePaste}
        {...props}
      />
    );
  }
);

SafeNameInput.displayName = 'SafeNameInput';
